<template>
  <d-container
    fluid
    class="main-content-container px-0 px-md-2 px-lg-4">
    <!-- Page Header -->
    <d-row
      v-if="!inPopup"
      no-gutters
      class="page-header py-4">
      <d-col
        sm="12"
        md="9"
        lg="9"
        class="text-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Klienci</span>
        <h3 class="page-title">Lista klientów</h3>
      </d-col>
      <d-col
        col
        sm="12"
        md="3"
        lg="3"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="$router.push('/klienci/nowy')">
          <i class="material-icons">add</i>
          Dodaj klienta
        </d-button>
      </d-col>
    </d-row>

    <d-row
      v-if="loaded && !inPopup"
      style="width: 100%"
      class="d-flex ml-0 mb-0 page-filters">
      <validation-observer
        ref="clients-filters-form"
        tag="form"
        style="width: 100%"
        class="d-flex mb-2">
        <d-input
          placeholder="Podaj imię i nazwisko, adres e-mail, telefon, nazwę firmy lub NIP klienta"
          v-model="filters.search"
          class="mr-1 mb-2"
          style="min-width: 290px;" />

        <v-select
          v-if="isAdmin"
          style="width: 360px; min-width: 150px; max-width: 30%;"
          class="mr-1 mb-2"
          :options="allWorkers"
          v-model="filters.workerID"
          :reduce="(worker) => worker.id"
          placeholder="Czyj klient?"
          :searchable="true"
          :filterBy="filterByForWorkers"
          :disabled="!loadedWorkers"
          label="name"
          @search="debouncedFetchWorkers">
          <template slot="no-options">
            <template v-if="!workersEmptyResults">Trwa wyszukiwanie&hellip;</template>
            <template v-else>Brak pracowników pasujących do podanej frazy&hellip;</template>
          </template>
          <template
            slot="option"
            slot-scope="option">
            <div class="d-center">
              {{ option.name }}
            </div>
          </template>
          <template
            slot="selected-option"
            slot-scope="option">
            <div class="selected d-center">
              {{ option.name }}
            </div>
          </template>
        </v-select>

        <v-select
          style="width: 360px; min-width: 170px; max-width: 30%;"
          class="mr-3 mb-2"
          :options="isWithDebtOptions"
          v-model="filters.isWithDebt"
          :reduce="(type) => type.value"
          placeholder="Zadłużenie"
          :searchable="false"
          label="text">
        </v-select>

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-md-auto mr-1 mb-2"
          @click.prevent="validateFiltersForm">
          Szukaj
        </d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap mb-2"
          @click.prevent="clearFilters">
          Resetuj filtry
        </d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0 align-middle">Klient</th>
            <th scope="col" class="border-0 align-middle text-center">Adres e-mail</th>
            <th scope="col" class="border-0 align-middle text-center">Telefon</th>
            <th scope="col" class="border-0 align-middle text-center">
              Obroty<br>
              z ostatnich 30 dni
            </th>
            <th
              scope="col"
              class="border-0 align-middle text-center">
              Zadłużenie
            </th>
            <th
              v-if="!inPopup"
              scope="col"
              class="border-0 align-middle text-center">
              Akcje
            </th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr v-for="(client, index) of clients" :key="'client-' + index">
            <td class="align-middle py-lg-1 py-md-3 py-3">
              <label class="table-mobile-label mb-1">Klient</label>
              {{ client.invoice_company }}<br>
              <small v-if="isAdmin && client.worker_name">
                Handlowiec: <strong>{{ client.worker_name }}</strong>
              </small>
            </td>
            <td class="align-middle text-center py-lg-1 py-md-3 py-3">
              <label class="table-mobile-label mb-1">Adres e-mail</label>
              <template v-if="client.email">
                <a :href="'mailto:' + client.email">
                  {{ client.email }}
                </a>
              </template>
              <template v-else>
                Brak
              </template>
            </td>
            <td class="align-middle text-center py-lg-1 py-md-3 py-3">
              <label class="table-mobile-label mb-1">Telefon</label>
              <template v-if="client.phone">
                <a :href="'tel:' + client.phone">
                  {{ client.phone }}
                </a>
              </template>
              <template v-else>
                Brak
              </template>
            </td>
            <td class="align-middle text-center py-lg-1 py-md-3 py-3">
              <label class="table-mobile-label mb-1">Obroty<br>z ostatnich 30 dni</label>
              <template v-if="client.sales_30_days">
                {{ roundProperly(client.sales_30_days) }} zł
              </template>
              <template v-else>
                Brak
              </template>
            </td>
            <td
              class="align-middle text-center py-lg-1 py-md-3 py-3">
              <label class="table-mobile-label mb-1">Zadłużenie</label>
              <span
                v-if="client.debt_amount > 0"
                class="font-weight-bold text-danger">
                {{ roundProperly(client.debt_amount) }} zł
              </span>
              <span
                v-else
                class="text-success">
                Niezadłużony
              </span>
            </td>

            <td
              v-if="!inPopup"
              class="text-center align-middle pt-0">
              <label class="table-mobile-label mb-1">Akcje</label>
              <d-button
                theme="primary"
                @click.native="$router.push('/klienci/edycja/' + client.id)"
                class="mx-2 mt-2"
                title="Edytuj">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removeClient(client.id)"
                title="Usuń"
                class="mx-2 mt-2">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="clients.length === 0">
            <td
              :colspan="inPopup ? 5 : 6"
              class="text-center">
              Brak użytkowników do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się.
        <a
          href="javascript:window.location.reload();"
          class="alert-link">
          Odśwież stronę
        </a>
        aby spróbować ponownie.
      </d-alert>
    </d-card>

    <pagination :meta-data="paginationMetaData" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import CustomDecimals from './../utils/CustomDecimals.js';

export default {
  name: 'clients',
  mixins: [HasFilters, HasPagination],
  components: {
    pagination: Pagination,
  },
  computed: {
    isWithDebtOptions() {
      return [
        {
          text: 'Dowolny stan zadłużenia',
          value: '',
        },
        {
          text: 'Klienci zadłużeni',
          value: '1',
        },
        {
          text: 'Klienci niezadłużeni',
          value: '0',
        },
      ];
    },
    isAdmin () {
      return this.$store.getters['getUserType'] === 'ADMIN';
    }
  },
  props: [
    'inPopup'
  ],
  data() {
    return {
      clients: [],
      allWorkers: [],
      loaded: false,
      loadError: false,
      removeInProgress: false,
      loadedWorkers: false,
      workersEmptyResults: false,
      filters: {
        search: '',
        isWithDebt: null,
        workerID: null
      },
    };
  },
  mounted() {
    if (this.inPopup) {
      this.filters.isWithDebt = 1;
    }
    this.loadFilteredData();
    if (this.isAdmin) {
      this.loadAdditionalData();
    }
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData() {
      let where = {};

      if (this.filters.search) {
        where.search = this.filters.search;
      }

      if (this.filters.isWithDebt) {
        where.with_debt = this.filters.isWithDebt;
      }

      if (this.filters.workerID) {
        where.worker_id = this.filters.workerID;
      }

      ListUtils.loadItemsData(this, {
        method: 'get',
        endpoint: '/api/contacts/items?page=' + this.currentPage,
        listField: 'clients',
        pagination: true,
        params: {
          where,
        },
      });
    },
    clearFilters() {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: '',
        isWithDebt: null,
        workerID: null
      };

      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm() {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(
        this.$refs['clients-filters-form'],
        this.debouncedLoadFilteredData
      );
    },
    removeClient(clientID) {
      this.removeInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/api/contacts/delete/',
        id: clientID,
        successTitle: 'Usunięto klienta',
        successText: 'Wybrany klient został usunięty',
        confirmTitle: 'Czy na pewno chcesz usunąć tego klienta?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie klienta nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        },
      });
    },
    loadAdditionalData() {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/users/items',
        method: 'get',
        outputKey: 'allWorkers',
        loadedKey: 'loadedWorkers',
        errorKey: 'loadError'
      });
    },
    filterByForWorkers (option, label, search) {
      return (option.name).toLocaleLowerCase('pl').indexOf(search.toLocaleLowerCase('pl')) > -1;
    },
    fetchWorkers (searchPhrase, loadingCallback) {
      this.emptyResults = false;
      loadingCallback(true);

      let endpointUrl = '/api/users/items';
      let responseAsArray = false;
      let paramsData = {};

      if (searchPhrase !== '') {
        paramsData.where = {
          search: searchPhrase
        };
      }

      if (this.filters.workerID !== 0 && this.filters.workerID !== null && searchPhrase === '') {
        endpointUrl = '/api/users/item/' + parseInt(this.filters.workerID, 10);
        responseAsArray = true;
      }

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: endpointUrl,
        outputKey: 'allWorkers',
        errorKey: 'loadError',
        params: paramsData,
        wrapResponseIntoArray: responseAsArray,
        successAction: (response) => {
          if (!response.length) {
            this.workersEmptyResults = true;
          }

          loadingCallback(false);
        }
      });
    },
    // eslint-disable-next-line
    debouncedFetchWorkers: debounce(function (searchPhrase, loadingCallback) {
      this.fetchWorkers(searchPhrase, loadingCallback);
    }, 500),
    roundProperly (value, decimals = 2) {
      return CustomDecimals.roundProperly(value, decimals);
    }
  },
};
</script>

